import { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { LoginPage } from "./pages/auth/login";
import { ForgotPasswordPage } from "./pages/auth/forgotPassword";
import { ResetPasswordPage } from "./pages/auth/resetPassword";
import { ProtectedRoute } from "./components/common/routing/protectedRoute";
import { RoleProtectedRoute } from "./components/common/routing/roleProtectedRoute";
import { decodeUserInfo } from "./utils/user";
import { getHomeRouteByRole } from "./utils/route-permissions";
import { ModuleProtectedRoute } from "./components/common/routing/moduleProtectedRoute";
import { useAuth } from "./contexts/auth/Context";
import { CommandPaletteProvider } from "./contexts/commandPalette/Provider";
import { UserProfilePage } from "./pages/user/profile";
import { DashboardHomePage } from "./pages/dashboard/home";
import { EnvIndicator } from "./components/common/ui/envIndicator";
import { config } from "./config/config";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { lazyWithRetry } from "./utils/lazyWithRetry";

const LazyCheckpointPinEntryPage = lazyWithRetry(
  () => import("./pages/checkpoint/pinEntry"),
  "CheckpointPinEntryPage",
);
const LazyCheckpointKioskPage = lazyWithRetry(
  () => import("./pages/checkpoint/checkpointKiosk"),
  "CheckpointKioskPage",
);
const LazyAboutPage = lazyWithRetry(
  () => import("./pages/about/about"),
  "AboutPage",
);
const LazyPaymentConfirmationPage = lazyWithRetry(
  () => import("./pages/payment/paymentConfirmation"),
  "PaymentConfirmationPage",
);
const LazyNotificationCenter = lazyWithRetry(
  () => import("./pages/notification/NotificationCenter"),
  "NotificationCenter",
);

const LazyGymsManagementPage = lazyWithRetry(
  () => import("./pages/admin/gym"),
  "GymsManagementPage",
);
const LazyUsersManagementPage = lazyWithRetry(
  () => import("./pages/admin/user"),
  "UsersManagementPage",
);
const LazyNewGymGuidePage = lazyWithRetry(
  () => import("./pages/admin/newGymGuide"),
  "NewGymGuidePage",
);
const LazyGymDetailPage = lazyWithRetry(
  () => import("./pages/admin/gymDetail"),
  "GymDetailPage",
);
const LazySuperadminDashboardPage = lazyWithRetry(
  () => import("./pages/admin/dashboard"),
  "SuperadminDashboardPage",
);
const LazyPlatformExpensesPage = lazyWithRetry(
  () => import("./pages/admin/platformExpenses"),
  "PlatformExpensesPage",
);
const LazyAdminLegalPage = lazyWithRetry(
  () => import("./pages/admin/legal"),
  "AdminLegalPage",
);
const LazyPlatformPlansPage = lazyWithRetry(
  () => import("./pages/admin/platformPlans"),
  "PlatformPlansPage",
);
const LazyGymSubscriptionsPage = lazyWithRetry(
  () => import("./pages/admin/gymSubscriptions"),
  "GymSubscriptionsPage",
);
const LazySalesHistoryPage = lazyWithRetry(
  () => import("./pages/admin/sales"),
  "SalesHistoryPage",
);
const LazyAdminGatewayTransactionsPage = lazyWithRetry(
  () => import("./pages/admin/gatewayTransactions"),
  "AdminGatewayTransactionsPage",
);
const LazyGymSubscriptionDetailPage = lazyWithRetry(
  () => import("./pages/admin/gymSubscriptionDetail"),
  "GymSubscriptionDetailPage",
);
const LazyMailTesterPage = lazyWithRetry(
  () => import("./pages/admin/mailTester"),
  "MailTesterPage",
);
const LazyAdminMailUsagePage = lazyWithRetry(
  () => import("./pages/admin/mailUsage"),
  "AdminMailUsagePage",
);
const LazyAdminAppBannersPage = lazyWithRetry(
  () => import("./pages/admin/appBanners"),
  "AdminAppBannersPage",
);
const LazyAdminAppReleasesPage = lazyWithRetry(
  () => import("./pages/admin/appReleases"),
  "AdminAppReleasesPage",
);
const LazyAdminAppReleaseSendPage = lazyWithRetry(
  () => import("./pages/admin/appReleaseSend"),
  "AdminAppReleaseSendPage",
);
const LazyAdminAppReleaseDetailPage = lazyWithRetry(
  () => import("./pages/admin/appReleaseDetail"),
  "AdminAppReleaseDetailPage",
);
const LazyMonitoringManagementPage = lazyWithRetry(
  () => import("./pages/monitoring/management"),
  "MonitoringManagementPage",
);

const LazyRegisterPlanPaymentPage = lazyWithRetry(
  () => import("./pages/plan/registerPlanPayment"),
  "RegisterPlanPaymentPage",
);
const LazyPlanManagementPage = lazyWithRetry(
  () => import("./pages/plan/management"),
  "PlanManagementPage",
);
const LazySubscriptionManagementPage = lazyWithRetry(
  () => import("./pages/subscritpion/management"),
  "SubscriptionManagementPage",
);
const LazySubscriptionDetailPage = lazyWithRetry(
  () => import("./pages/subscritpion/detail"),
  "SubscriptionDetailPage",
);

const LazyClientsManagementPage = lazyWithRetry(
  () => import("./pages/client/management"),
  "ClientsManagementPage",
);
const LazyClientDetailPage = lazyWithRetry(
  () => import("./pages/client/detail"),
  "ClientDetailPage",
);

const LazyRegisterFoodPaymentPage = lazyWithRetry(
  () => import("./pages/food/registerFoodPayment"),
  "default",
);
const LazyFoodManagementPage = lazyWithRetry(
  () => import("./pages/food/management"),
  "FoodManagementPage",
);
const LazyRegisterMerchandisePaymentPage = lazyWithRetry(
  () => import("./pages/merchandise/registerMerchandisePayment"),
  "default",
);
const LazyMerchandiseManagementPage = lazyWithRetry(
  () => import("./pages/merchandise/management"),
  "MerchandiseManagementPage",
);
const LazySupplierManagementPage = lazyWithRetry(
  () => import("./pages/supplier/management"),
  "SupplierManagementPage",
);

const LazyAccessControlPage = lazyWithRetry(
  () => import("./pages/accessControl/management"),
  "AccessControlPage",
);
const LazyBookingManagementPage = lazyWithRetry(
  () => import("./pages/booking/management"),
  "BookingManagementPage",
);
const LazyTrainerManagementPage = lazyWithRetry(
  () => import("./pages/trainer/management"),
  "TrainerManagementPage",
);
const LazyShiftManagementPage = lazyWithRetry(
  () => import("./pages/shift/management"),
  "ShiftManagementPage",
);
const LazyTrainerDetailPage = lazyWithRetry(
  () => import("./pages/trainer/detail"),
  "TrainerDetailPage",
);

const LazyInventoryManagementPage = lazyWithRetry(
  () => import("./pages/inventory/management"),
  "InventoryManagementPage",
);
const LazyMachineryManagementPage = lazyWithRetry(
  () => import("./pages/machinary/management"),
  "MachineryManagementPage",
);
const LazyMaterialManagementPage = lazyWithRetry(
  () => import("./pages/material/management"),
  "MaterialManagementPage",
);
const LazyProductManagementPage = lazyWithRetry(
  () => import("./pages/product/management"),
  "ProductManagementPage",
);

const LazyExpensesManagementPage = lazyWithRetry(
  () => import("./pages/expense/management"),
  "ExpensesManagementPage",
);
const LazyHistoricalExpensesPage = lazyWithRetry(
  () => import("./pages/expense/historicalExpenses"),
  "HistoricalExpensesPage",
);
const LazyOtherIncomesManagementPage = lazyWithRetry(
  () => import("./pages/incomes/otherIncomes/management"),
  "OtherIncomesManagementPage",
);
const LazyUnifiedIncomesManagementPage = lazyWithRetry(
  () => import("./pages/incomes/unifiedIncomesManagement"),
  "UnifiedIncomesManagementPage",
);

const LazyMetricsManagementPage = lazyWithRetry(
  () => import("./pages/metrics/management"),
  "MetricsManagementPage",
);
const LazyProductionMetricsManagementPage = lazyWithRetry(
  () => import("./pages/productionMetrics/management"),
  "ProductionMetricsManagementPage",
);

const LazyGatewayManagementPage = lazyWithRetry(
  () => import("./pages/gateway/management"),
  "GatewayManagementPage",
);
const LazyGatewaySubscriptionDetailPage = lazyWithRetry(
  () => import("./pages/gatewaySubscription/detail"),
  "GatewaySubscriptionDetailPage",
);
const LazyCreateGatewaySubscriptionPage = lazyWithRetry(
  () => import("./pages/gatewaySubscription/create"),
  "CreateGatewaySubscriptionPage",
);

const LazySalesOrderManagementPage = lazyWithRetry(
  () => import("./pages/salesOrder/management"),
  "SalesOrderManagementPage",
);
const LazySalesOrderDetailPage = lazyWithRetry(
  () => import("./pages/salesOrder/detail"),
  "SalesOrderDetailPage",
);
const LazyInvoiceManagementPage = lazyWithRetry(
  () => import("./pages/invoice/management"),
  "InvoiceManagementPage",
);
const LazyInvoiceDetailPage = lazyWithRetry(
  () => import("./pages/invoice/detail"),
  "InvoiceDetailPage",
);

const LazyB2bCrmManagementPage = lazyWithRetry(
  () => import("./pages/b2bCrm/management"),
  "B2bCrmManagementPage",
);
const LazySendCampaignPage = lazyWithRetry(
  () => import("./pages/campaign/send"),
  "SendCampaignPage",
);
const LazyCampaignDashboardPage = lazyWithRetry(
  () => import("./pages/campaign/dashboard"),
  "CampaignDashboardPage",
);
const LazyCampaignDetailPage = lazyWithRetry(
  () => import("./pages/campaign/detail"),
  "CampaignDetailPage",
);
const LazyAppBannersPage = lazyWithRetry(
  () => import("./pages/clientApp/appBanners"),
  "AppBannersPage",
);

const LazyClientImportPage = lazyWithRetry(
  () => import("./pages/import/ClientImportPage"),
  "ClientImportPage",
);
const LazyPlanImportPage = lazyWithRetry(
  () => import("./pages/import/PlanImportPage"),
  "PlanImportPage",
);
const LazyActiveSubscriptionImportPage = lazyWithRetry(
  () => import("./pages/import/ActiveSubscriptionImportPage"),
  "ActiveSubscriptionImportPage",
);
const LazyBillingManagementPage = lazyWithRetry(
  () => import("./pages/billing/management"),
  "BillingManagementPage",
);
const LazyBillingEnrollmentReturnPage = lazyWithRetry(
  () => import("./pages/billing/enrollmentReturn"),
  "BillingEnrollmentReturnPage",
);
const LazyHelpPage = lazyWithRetry(
  () => import("./pages/help/help"),
  "HelpPage",
);
const LazyTrainerRegisterPage = lazyWithRetry(
  () => import("./pages/trainerAccount/register"),
  "TrainerRegisterPage",
);
const LazyTrainerVerifyPage = lazyWithRetry(
  () => import("./pages/trainerAccount/verify"),
  "TrainerVerifyPage",
);
const LazyTrainerLoginPage = lazyWithRetry(
  () => import("./pages/trainerAccount/login"),
  "TrainerLoginPage",
);
const LazyTrainerForgotPasswordPage = lazyWithRetry(
  () => import("./pages/trainerAccount/forgotPassword"),
  "TrainerForgotPasswordPage",
);
const LazyTrainerResetPasswordPage = lazyWithRetry(
  () => import("./pages/trainerAccount/resetPassword"),
  "TrainerResetPasswordPage",
);
const LazyTrainerInvitationsPage = lazyWithRetry(
  () => import("./pages/trainerAccount/invitations"),
  "TrainerInvitationsPage",
);
const LazyTrainerSchedulePage = lazyWithRetry(
  () => import("./pages/trainerAccount/schedule"),
  "TrainerSchedulePage",
);
const LazyTrainerProfilePage = lazyWithRetry(
  () => import("./pages/trainerAccount/profile"),
  "TrainerProfilePage",
);
const LazyTrainerSummaryPage = lazyWithRetry(
  () => import("./pages/trainerAccount/summary"),
  "TrainerSummaryPage",
);
const LazyTrainerPlansPage = lazyWithRetry(
  () => import("./pages/trainerAccount/plans"),
  "TrainerPlansPage",
);
const LazyTrainerClientsPage = lazyWithRetry(
  () => import("./pages/trainerAccount/clients"),
  "TrainerClientsPage",
);
const LazyTrainerClassesPage = lazyWithRetry(
  () => import("./pages/trainerAccount/classes"),
  "TrainerClassesPage",
);
const LazyTrainerServicesPage = lazyWithRetry(
  () => import("./pages/trainerAccount/services"),
  "TrainerServicesPage",
);
const LazyTrainerSalesPage = lazyWithRetry(
  () => import("./pages/trainerAccount/sales"),
  "TrainerSalesPage",
);
const LazyTrainerHomePage = lazyWithRetry(
  () => import("./pages/trainerAccount/home"),
  "TrainerHomePage",
);
const LazyTrainerExercisesPage = lazyWithRetry(
  () => import("./pages/trainerAccount/planning/exercises"),
  "TrainerExercisesPage",
);
const LazyTrainerProgramsPage = lazyWithRetry(
  () => import("./pages/trainerAccount/planning/programs"),
  "TrainerProgramsPage",
);
const LazyTrainerProgramDetailPage = lazyWithRetry(
  () => import("./pages/trainerAccount/planning/programDetail"),
  "TrainerProgramDetailPage",
);
const LazyTrainerProgramBlockPage = lazyWithRetry(
  () => import("./pages/trainerAccount/planning/programBlock"),
  "TrainerProgramBlockPage",
);
const LazyTrainerSessionDetailPage = lazyWithRetry(
  () => import("./pages/trainerAccount/planning/sessionDetail"),
  "TrainerSessionDetailPage",
);

function RouteFallback() {
  return (
    <div className="h-screen bg-global-primary flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-global-accent border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/** Ruta vieja del correo de verificación: redirige conservando el `?token=`. */
function TrainerVerifyRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/auth/trainer/verify${search}`} replace />;
}

/** Manda a cada rol a su inicio. El del entrenador no es el del panel. */
function HomeRedirect() {
  const { token } = useAuth();
  const { role } = decodeUserInfo(token);
  return <Navigate to={getHomeRouteByRole(role)} replace />;
}

function App() {
  const { authenticated } = useAuth();

  return (
    <Router>
      <EnvIndicator />
      <CommandPaletteProvider>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* RUTAS PÚBLICAS */}
            <Route path="/auth" element={<LoginPage />} />
            <Route
              path="/auth/forgot-password"
              element={<ForgotPasswordPage />}
            />
            <Route
              path="/auth/reset-password"
              element={<ResetPasswordPage />}
            />
            <Route path="/about" element={<LazyAboutPage />} />
            <Route
              path="/payment/confirmation"
              element={<LazyPaymentConfirmationPage />}
            />
            <Route
              path="/checkpoint/:gymSlug"
              element={<LazyCheckpointPinEntryPage />}
            />
            <Route
              path="/checkpoint/:gymSlug/kiosk"
              element={<LazyCheckpointKioskPage />}
            />
            {/* Acceso propio del entrenador, separado del panel. */}
            <Route path="/auth/trainer" element={<LazyTrainerLoginPage />} />
            <Route
              path="/auth/trainer/verify"
              element={<LazyTrainerVerifyPage />}
            />
            <Route
              path="/auth/trainer/forgot-password"
              element={<LazyTrainerForgotPasswordPage />}
            />
            <Route
              path="/auth/trainer/reset-password"
              element={<LazyTrainerResetPasswordPage />}
            />
            {/* Ruta vieja: un correo de verificación ya enviado apunta acá. Se
                redirige conservando el `?token=`. */}
            <Route
              path="/trainer-account/verify"
              element={<TrainerVerifyRedirect />}
            />
            <Route
              path="/auth/trainer/register"
              element={<LazyTrainerRegisterPage />}
            />
            <Route
              path="/trainer-account/register"
              element={<Navigate to="/auth/trainer/register" replace />}
            />

            {/* RUTAS PROTEGIDAS (requieren autenticación) */}
            <Route element={<ProtectedRoute isAuthenticated={authenticated} />}>
              {/* ESPACIO DEL ENTRENADOR (sin gimnasio, layout propio) */}
              <Route
                element={<RoleProtectedRoute allowedRoles={["TRAINER"]} />}
              >
                <Route
                  path="/trainer-account"
                  element={<Navigate to="/trainer-account/home" replace />}
                />
                <Route
                  path="/trainer-account/home"
                  element={<LazyTrainerHomePage />}
                />
                <Route
                  path="/trainer-account/invitations"
                  element={<LazyTrainerInvitationsPage />}
                />
                <Route
                  path="/trainer-account/schedule"
                  element={<LazyTrainerSchedulePage />}
                />
                <Route
                  path="/trainer-account/clients"
                  element={<LazyTrainerClientsPage />}
                />
                <Route
                  path="/trainer-account/classes"
                  element={<LazyTrainerClassesPage />}
                />
                <Route
                  path="/trainer-account/services"
                  element={<LazyTrainerServicesPage />}
                />
                <Route
                  path="/trainer-account/sales"
                  element={<LazyTrainerSalesPage />}
                />
                <Route
                  path="/trainer-account/planning"
                  element={
                    <Navigate to="/trainer-account/planning/programs" replace />
                  }
                />
                <Route
                  path="/trainer-account/planning/exercises"
                  element={<LazyTrainerExercisesPage />}
                />
                <Route
                  path="/trainer-account/planning/programs"
                  element={<LazyTrainerProgramsPage />}
                />
                <Route
                  path="/trainer-account/planning/programs/:publicId"
                  element={<LazyTrainerProgramDetailPage />}
                />
                <Route
                  path="/trainer-account/planning/program-blocks/:publicId"
                  element={<LazyTrainerProgramBlockPage />}
                />
                <Route
                  path="/trainer-account/planning/programs/:publicId/sessions/:sessionPublicId"
                  element={<LazyTrainerSessionDetailPage />}
                />
                <Route
                  path="/trainer-account/summary"
                  element={<LazyTrainerSummaryPage />}
                />
                <Route
                  path="/trainer-account/profile"
                  element={<LazyTrainerProfilePage />}
                />
                <Route
                  path="/trainer-account/plans"
                  element={<LazyTrainerPlansPage />}
                />
              </Route>

              {/* RUTAS COMUNES DEL PANEL. El entrenador no entra: renderizan
                  PageLayout, que asume gimnasio. */}
              <Route
                element={
                  <RoleProtectedRoute
                    allowedRoles={["SUPERADMIN", "MANAGER", "STAFF"]}
                  />
                }
              >
                <Route path="/profile" element={<UserProfilePage />} />
                <Route path="/help" element={<LazyHelpPage />} />
              </Route>

              {/* Notificaciones: MANAGER y STAFF ven las de su gym; el
                  SUPERADMIN ve la bandeja de plataforma. */}
              <Route
                element={
                  <RoleProtectedRoute
                    allowedRoles={["MANAGER", "STAFF", "SUPERADMIN"]}
                  />
                }
              >
                <Route
                  path="/notifications"
                  element={<LazyNotificationCenter />}
                />
              </Route>

              {/* RUTAS SUPERADMIN Acceso: Mi perfil, Administración, Monitoreo */}
              <Route
                element={<RoleProtectedRoute allowedRoles={["SUPERADMIN"]} />}
              >
                <Route
                  path="/admin/dashboard"
                  element={<LazySuperadminDashboardPage />}
                />
                <Route
                  path="/admin/gyms"
                  element={<LazyGymsManagementPage />}
                />
                <Route
                  path="/admin/gym/:publicId"
                  element={<LazyGymDetailPage />}
                />
                <Route
                  path="/admin/users"
                  element={<LazyUsersManagementPage />}
                />
                <Route
                  path="/admin/new-gym-guide"
                  element={<LazyNewGymGuidePage />}
                />
                <Route
                  path="/admin/expenses"
                  element={<LazyPlatformExpensesPage />}
                />
                <Route
                  path="/admin/plans"
                  element={<LazyPlatformPlansPage />}
                />
                <Route path="/admin/legal" element={<LazyAdminLegalPage />} />
                <Route
                  path="/admin/subscriptions"
                  element={<LazyGymSubscriptionsPage />}
                />
                <Route path="/admin/sales" element={<LazySalesHistoryPage />} />
                <Route
                  path="/admin/gateway-transactions"
                  element={<LazyAdminGatewayTransactionsPage />}
                />
                <Route
                  path="/admin/subscriptions/:gymPublicId"
                  element={<LazyGymSubscriptionDetailPage />}
                />
                <Route
                  path="/admin/mail-tester"
                  element={<LazyMailTesterPage />}
                />
                <Route
                  path="/admin/mail-usage"
                  element={<LazyAdminMailUsagePage />}
                />
                <Route
                  path="/admin/app-banners"
                  element={<LazyAdminAppBannersPage />}
                />
                <Route
                  path="/admin/app-releases"
                  element={<LazyAdminAppReleasesPage />}
                />
                <Route
                  path="/admin/app-releases/new"
                  element={<LazyAdminAppReleaseSendPage />}
                />
                <Route
                  path="/admin/app-releases/:publicId"
                  element={<LazyAdminAppReleaseDetailPage />}
                />
                <Route
                  path="/monitoring"
                  element={<LazyMonitoringManagementPage />}
                />
              </Route>

              {/* RUTAS STAFF Acceso: Dashboard, ventas, clientes, planes, catálogos básicos */}
              <Route
                element={
                  <RoleProtectedRoute allowedRoles={["MANAGER", "STAFF"]} />
                }
              >
                <Route path="/" element={<DashboardHomePage />} />
                <Route
                  element={<ModuleProtectedRoute module="accessControl" />}
                >
                  <Route
                    path="/access-control"
                    element={<LazyAccessControlPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="planSales" />}>
                  <Route
                    path="/plan-payments"
                    element={<LazyRegisterPlanPaymentPage />}
                  />
                  <Route path="/plans" element={<LazyPlanManagementPage />} />
                  <Route
                    path="/subscriptions"
                    element={<LazySubscriptionManagementPage />}
                  />
                  <Route
                    path="/subscriptions/:publicId"
                    element={<LazySubscriptionDetailPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="foodSales" />}>
                  <Route
                    path="/food-sales"
                    element={<LazyRegisterFoodPaymentPage />}
                  />
                  <Route path="/foods" element={<LazyFoodManagementPage />} />
                </Route>
                <Route
                  element={<ModuleProtectedRoute module="merchandiseSales" />}
                >
                  <Route
                    path="/merchandise-sales"
                    element={<LazyRegisterMerchandisePaymentPage />}
                  />
                  <Route
                    path="/merchandise"
                    element={<LazyMerchandiseManagementPage />}
                  />
                </Route>
                <Route
                  path="/clients"
                  element={<LazyClientsManagementPage />}
                />
                <Route
                  path="/client/:publicId"
                  element={<LazyClientDetailPage />}
                />
                <Route element={<ModuleProtectedRoute module="bookings" />}>
                  <Route
                    path="/bookings"
                    element={<LazyBookingManagementPage />}
                  />
                </Route>
                <Route
                  element={<ModuleProtectedRoute module="shiftManagement" />}
                >
                  <Route path="/shifts" element={<LazyShiftManagementPage />} />
                </Route>
                {/* STAFF puede crear cobros recurrentes (venta desde el perfil
                  del cliente); la gestión de Pagos Online sigue MANAGER-only */}
                <Route
                  element={<ModuleProtectedRoute module="onlinePayments" />}
                >
                  <Route
                    path="/gateway-subscriptions/new"
                    element={<LazyCreateGatewaySubscriptionPage />}
                  />
                </Route>
              </Route>

              {/* RUTAS MANAGER (exclusivas) Acceso: Finanzas, inventario, maquinaria, métricas, entrenadores */}
              <Route
                element={<RoleProtectedRoute allowedRoles={["MANAGER"]} />}
              >
                <Route element={<ModuleProtectedRoute module="trainers" />}>
                  <Route
                    path="/trainers"
                    element={<LazyTrainerManagementPage />}
                  />
                  <Route
                    path="/trainer/:publicId"
                    element={<LazyTrainerDetailPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="inventory" />}>
                  <Route
                    path="/inventory"
                    element={<LazyInventoryManagementPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="machinery" />}>
                  <Route
                    path="/machinery"
                    element={<LazyMachineryManagementPage />}
                  />
                </Route>
                <Route
                  element={<ModuleProtectedRoute module="productCatalog" />}
                >
                  <Route
                    path="/materials"
                    element={<LazyMaterialManagementPage />}
                  />
                  <Route
                    path="/products"
                    element={<LazyProductManagementPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="suppliers" />}>
                  <Route
                    path="/suppliers"
                    element={<LazySupplierManagementPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="salesOrders" />}>
                  <Route
                    path="/sales-orders"
                    element={<LazySalesOrderManagementPage />}
                  />
                  <Route
                    path="/sales-orders/:publicId"
                    element={<LazySalesOrderDetailPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="invoicing" />}>
                  <Route
                    path="/invoices"
                    element={<LazyInvoiceManagementPage />}
                  />
                  <Route
                    path="/invoices/:publicId"
                    element={<LazyInvoiceDetailPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="expenses" />}>
                  <Route
                    path="/expenses"
                    element={<LazyExpensesManagementPage />}
                  />
                  <Route
                    path="/expenses-history"
                    element={<LazyHistoricalExpensesPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="otherIncome" />}>
                  <Route
                    path="/other-incomes"
                    element={<LazyOtherIncomesManagementPage />}
                  />
                </Route>
                <Route
                  element={
                    <ModuleProtectedRoute
                      module={[
                        "planSales",
                        "foodSales",
                        "merchandiseSales",
                        "otherIncome",
                      ]}
                    />
                  }
                >
                  <Route
                    path="/unified-incomes"
                    element={<LazyUnifiedIncomesManagementPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="metrics" />}>
                  <Route
                    path="/metrics"
                    element={<LazyMetricsManagementPage />}
                  />
                </Route>
                <Route
                  element={<ModuleProtectedRoute module="onlinePayments" />}
                >
                  <Route
                    path="/gateway-transactions"
                    element={<LazyGatewayManagementPage />}
                  />
                  <Route
                    path="/gateway-subscriptions"
                    element={
                      <Navigate
                        to="/gateway-transactions?tab=subscriptions"
                        replace
                      />
                    }
                  />
                  <Route
                    path="/gateway-subscriptions/:publicId"
                    element={<LazyGatewaySubscriptionDetailPage />}
                  />
                </Route>
                <Route
                  element={<ModuleProtectedRoute module="productionMetrics" />}
                >
                  <Route
                    path="/production-metrics"
                    element={<LazyProductionMetricsManagementPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="b2bCrm" />}>
                  <Route
                    path="/b2b-crm"
                    element={<LazyB2bCrmManagementPage />}
                  />
                </Route>
                <Route
                  element={<ModuleProtectedRoute module="emailCampaigns" />}
                >
                  <Route
                    path="/campaigns"
                    element={<LazyCampaignDashboardPage />}
                  />
                  <Route
                    path="/campaigns/send"
                    element={<LazySendCampaignPage />}
                  />
                  <Route
                    path="/campaigns/:publicId"
                    element={<LazyCampaignDetailPage />}
                  />
                </Route>
                <Route element={<ModuleProtectedRoute module="clientApp" />}>
                  <Route path="/app-banners" element={<LazyAppBannersPage />} />
                </Route>
                <Route
                  path="/import/clients"
                  element={<LazyClientImportPage />}
                />
                <Route path="/import/plans" element={<LazyPlanImportPage />} />
                <Route
                  path="/import/active-subscriptions"
                  element={<LazyActiveSubscriptionImportPage />}
                />
                {/* Vertical de cobro recurrente: solo visible en development */}
                {config.isDevelopment && (
                  <>
                    <Route
                      path="/billing"
                      element={<LazyBillingManagementPage />}
                    />
                    <Route
                      path="/billing/enrollment/return"
                      element={<LazyBillingEnrollmentReturnPage />}
                    />
                  </>
                )}
              </Route>

              {/* Rutas no existentes: al inicio que le corresponda al rol.
                  Mandar todo a /profile dejaba al entrenador en el layout del
                  gimnasio, que asume que hay uno. */}
              <Route path="*" element={<HomeRedirect />} />
            </Route>
          </Routes>
        </Suspense>
      </CommandPaletteProvider>
      <SpeedInsights />
    </Router>
  );
}

export default App;
